import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { ExistingUserData } from '@/domains/user/components/ExistingUserInfoCard/types';
import { CreateClaimantAutomaticallyRequest } from '../types/api/requests/CreateClaimantAutomaticallyRequest';
import { CreateClaimantManuallyRequest } from '../types/api/requests/CreateClaimantManuallyRequest';
import {
  CreateClaimantFieldKey,
  CreateClaimantFieldValue,
  CreateClaimantState,
  CreateClaimantType,
} from './types/createClaimantSliceTypes';
import { AddClaimantModalSteps } from '@/features/serviceModal/components/AddClaimant/enums';
import { createClaimantInitialState } from './constants/createClaimantSliceConstants';

const createClaimantSlice = createSlice({
  name: 'createClaimant',
  initialState: createClaimantInitialState,
  reducers: {
    changeCreateClaimantType: (state: CreateClaimantState, action: PayloadAction<CreateClaimantType>) => {
      state.createClaimantType = action.payload;
    },
    updateCreateClaimantStepValues: <K extends CreateClaimantFieldKey>(
      state: CreateClaimantState,
      action: PayloadAction<{ step: K; value: CreateClaimantFieldValue<K> }>,
    ) => {
      const { step, value } = action.payload;
      state.createClaimantSteps[step] = value;
    },
    resetCreateClaimantState: () => createClaimantInitialState,
    setUserForClaimantCreate: (state: CreateClaimantState, action: PayloadAction<ExistingUserData | null>) => {
      state.userForClaimantCreate = action.payload;
    },
    createClaimantManually: (_, _action: PayloadAction<CreateClaimantManuallyRequest>) => {},
    createClaimantAutomatically: (_, _action: PayloadAction<CreateClaimantAutomaticallyRequest>) => {},
    createClaimantSuccess: () => {},
    setCreateClaimantLoadingStatus: (state, { payload }: PayloadAction<boolean>) => {
      state.statuses.isCreateClaimantLoading = payload;
    },
  },
  selectors: {
    selectCreateClaimantSteps: (state) => state.createClaimantSteps,
    selectUserForClaimantCreate: (state) => state.userForClaimantCreate,
    selectCreateClaimantType: (state) => state.createClaimantType,
    selectCreateClaimantIsLoading: (state) => state.statuses.isCreateClaimantLoading,
    selectCreateClaimantAddressDetails: (state) => state.createClaimantSteps[AddClaimantModalSteps.AddressInfo],
  },
});

export const {
  updateCreateClaimantStepValues,
  resetCreateClaimantState,
  setUserForClaimantCreate,
  changeCreateClaimantType,
  createClaimantAutomatically,
  createClaimantManually,
  setCreateClaimantLoadingStatus,
  createClaimantSuccess,
} = createClaimantSlice.actions;

export const {
  selectCreateClaimantSteps,
  selectUserForClaimantCreate,
  selectCreateClaimantType,
  selectCreateClaimantIsLoading,
  selectCreateClaimantAddressDetails,
} = createClaimantSlice.selectors;

export const createClaimantReducer = createClaimantSlice.reducer;
export const createClaimantReducerPath = createClaimantSlice.reducerPath;
